import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Award, GraduationCap, Loader2, Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { DateField, isoDay } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const API_BASE = `${BACKEND_URL}/api`;

const STATUS_STYLE = {
  applied: "border-border bg-muted text-muted-foreground",
  enrolled: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  in_placement: "border-primary/30 bg-primary/10 text-primary",
  review: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  completed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  withdrawn: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};
const OUTCOME_STYLE = {
  pass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  concerns: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  fail: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};
const SCOPES = [["website", "Website"], ["print", "Printed material"], ["social", "Social media"]];

const BLANK = {
  name: "", email: "", phone: "", date_of_birth: "", address: "", education: "",
  family_background: "", guardian_name: "", guardian_phone: "", trade: "", funding_source: "",
  stipend_monthly: 8000, consent_granted: false, consent_scopes: [], notes: "",
};
const BLANK_PLACEMENT = {
  department: "", programme_code: "", supervisor: "", supervisor_email: "",
  starts_on: isoDay(new Date()), ends_on: "", funding_source: "", location: "",
};

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground">{hint}</p>}
  </div>
);

export default function Apprenticeships() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [programmes, setProgrammes] = useState([]);
  const [creating, setCreating] = useState(null);
  const [open, setOpen] = useState(null);
  const [placement, setPlacement] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ outcome: "pass", rating: 4, note: "",
    evidence_reference: "" });
  const [extra, setExtra] = useState({ label: "", due_on: isoDay(new Date()) });
  const [closing, setClosing] = useState(null);
  const [reason, setReason] = useState("");
  const [portal, setPortal] = useState(null);
  const [portalPassword, setPortalPassword] = useState("");
  const [busy, setBusy] = useState("");
  const canWrite = can("apprentice", "write");

  const load = useCallback(async () => {
    const [list, config, progs] = await Promise.all([
      api.get("/apprenticeship/apprentices"),
      api.get("/apprenticeship/config"),
      api.get("/charity/programs"),
    ]);
    setData(list.data);
    setCfg(config.data);
    setProgrammes(progs.data || []);
    return list.data;
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    let ok = false;
    try {
      const res = await fn();
      toast.success(typeof message === "function" ? message(res) : message);
      const fresh = await load();
      if (open) {
        const again = fresh.items.find((i) => i.apprentice_ref === open.apprentice_ref);
        if (again) setOpen(again);
      }
      ok = true;
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
    return ok;
  };

  const create = async () => {
    const ok = await act("create", () => api.post("/apprenticeship/apprentices", {
      ...creating, stipend_monthly: Number(creating.stipend_monthly) || 0,
      consent_scopes: creating.consent_granted ? creating.consent_scopes : [],
    }), (res) => `${res.data.apprentice_ref} created`);
    if (ok) setCreating(null);
  };

  const uploadPhoto = async (ref, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    await act(`photo-${ref}`, () => api.post(`/apprenticeship/apprentices/${ref}/photo`, body,
      { headers: { "Content-Type": "multipart/form-data" } }), "Photograph saved");
  };

  const startPlacement = async () => {
    const ok = await act("placement", () => api.post(
      `/apprenticeship/apprentices/${placement.ref}/placement`, {
        ...placement.form,
        stipend_monthly: placement.form.stipend_monthly === ""
          ? null : Number(placement.form.stipend_monthly),
      }), "Placement started and the review points laid out");
    if (ok) setPlacement(null);
  };

  const recordReview = async () => {
    const ok = await act("review", () => api.post(
      `/apprenticeship/apprentices/${review.ref}/reviews/${review.index}`, reviewForm),
      (res) => res.data.message);
    if (ok) { setReview(null); setReviewForm({ outcome: "pass", rating: 4, note: "",
      evidence_reference: "" }); }
  };

  const move = async (ref, status) => {
    const ok = await act(`status-${ref}`, () => api.post(
      `/apprenticeship/apprentices/${ref}/status`, { status, reason }),
      `Recorded as ${status.replace(/_/g, " ")}`);
    if (ok) { setClosing(null); setReason(""); }
  };

  if (!data || !cfg) return <Loading label="Loading apprenticeships" />;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Phase 6 · Apprenticeships"
        title="Apprentice profiles, placements and reviews"
        description="Each apprentice carries their own profile, a placement with a named supervisor, a training plan and review points laid out from your own intervals. A failed review is recorded and flagged, never hidden — and it does not block progression."
        actions={canWrite && (
          <Button data-testid="new-apprentice-button" onClick={() => setCreating({
            ...BLANK, stipend_monthly: cfg.default_stipend })}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="mr-1.5 h-4 w-4" /> Add an apprentice
          </Button>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi testId="kpi-apprentices" label="Apprentices" value={data.counters.total}
             icon={GraduationCap} />
        <Kpi testId="kpi-in-placement" label="On placement" value={data.counters.in_placement}
             accent="teal" />
        <Kpi testId="kpi-reviews-overdue" label="Reviews overdue"
             value={data.counters.reviews_overdue}
             accent={data.counters.reviews_overdue ? "amber" : "emerald"} />
        <Kpi testId="kpi-completed" label="Completed" value={data.counters.completed}
             accent="emerald" icon={Award} />
        <Kpi testId="kpi-stipend" label="Monthly stipend" accent="violet"
             value={money(data.counters.stipend_monthly, "INR", true)} />
      </div>

      <Tabs defaultValue="register">
        <TabsList data-testid="apprentice-tabs">
          <TabsTrigger value="register" data-testid="tab-apprentices">Apprentices</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-apprentice-settings">
            Review intervals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="register" className="mt-4">
          <Panel title="Register" testId="panel-apprentices">
            <Table testId="apprentices-table" head={<><Th>Reference</Th><Th>Apprentice</Th>
              <Th>Trade</Th><Th>Placement</Th><Th>Reviews</Th><Th>Stipend</Th><Th>Status</Th>
              <Th /></>}>
              {data.items.length === 0 && <Empty label="No apprentices yet" colSpan={8} />}
              {data.items.map((a) => (
                <tr key={a.apprentice_ref} className="border-b border-border/60"
                    data-testid={`apprentice-row-${a.apprentice_ref}`}>
                  <Td className="font-mono text-[11px]">{a.apprentice_ref}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      {a.photo_url
                        ? <img alt="" data-testid={`apprentice-photo-${a.apprentice_ref}`}
                               src={`${API_BASE}${a.photo_url.replace("/api", "")}`}
                               className="h-7 w-7 rounded-full object-cover" />
                        : <span className="grid h-7 w-7 place-items-center rounded-full bg-muted font-mono text-[10px] text-muted-foreground">
                            {a.name.slice(0, 2).toUpperCase()}
                          </span>}
                      <span>{a.name}</span>
                      {a.flagged && (
                        <Chip className={OUTCOME_STYLE.fail} testId={`flagged-${a.apprentice_ref}`}>
                          concern recorded
                        </Chip>
                      )}
                    </div>
                  </Td>
                  <Td className="text-[12px]">{a.trade || "—"}</Td>
                  <Td className="text-[11.5px] text-muted-foreground">
                    {a.placement
                      ? `${a.placement.department} · ${a.placement.supervisor}`
                      : "Not placed"}
                  </Td>
                  <Td className="font-mono text-[11px]">
                    {a.reviews_done}/{(a.reviews || []).length}
                    {a.reviews_overdue > 0 && (
                      <span className="ml-1 text-amber-600 dark:text-amber-400">
                        ({a.reviews_overdue} due)
                      </span>
                    )}
                  </Td>
                  <Td className="font-mono text-[11px]">
                    {money(a.stipend_monthly || 0, "INR", true)}
                  </Td>
                  <Td><Chip className={STATUS_STYLE[a.status]}
                            testId={`apprentice-status-${a.apprentice_ref}`}>
                    {a.status.replace(/_/g, " ")}</Chip></Td>
                  <Td>
                    <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                            data-testid={`open-${a.apprentice_ref}`} onClick={() => setOpen(a)}>
                      Open
                    </Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <Panel title="Review intervals and defaults" testId="panel-apprentice-settings"
                 kicker="Applied when a placement starts; the coordinator can add more review points per apprentice">
            <div className="grid gap-4 lg:grid-cols-2">
              <F label="Review points, in weeks from the start (comma separated)">
                <Input value={(cfg.review_weeks || []).join(", ")} disabled={!canWrite}
                       data-testid="review-weeks-input"
                       onChange={(e) => setCfg({
                         ...cfg,
                         review_weeks: e.target.value.split(",").map((v) => parseInt(v.trim(), 10))
                           .filter((v) => !Number.isNaN(v)),
                       })} />
              </F>
              <F label="Default placement length (weeks)">
                <Input type="number" value={cfg.default_duration_weeks} disabled={!canWrite}
                       data-testid="duration-input"
                       onChange={(e) => setCfg({ ...cfg,
                         default_duration_weeks: Number(e.target.value) })} />
              </F>
              <F label="Default monthly stipend">
                <Input type="number" value={cfg.default_stipend} disabled={!canWrite}
                       data-testid="stipend-input"
                       onChange={(e) => setCfg({ ...cfg, default_stipend: Number(e.target.value) })} />
              </F>
              <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                <span className="text-[12.5px] text-foreground">
                  Add a final review at the end of the placement
                </span>
                <Switch checked={cfg.final_review} disabled={!canWrite}
                        data-testid="final-review-switch"
                        onCheckedChange={(v) => setCfg({ ...cfg, final_review: v })} />
              </div>
              <F label="Training plan modules (one per line)">
                <Textarea rows={4} value={(cfg.modules || []).join("\n")} disabled={!canWrite}
                          data-testid="modules-input"
                          onChange={(e) => setCfg({ ...cfg,
                            modules: e.target.value.split("\n").map((m) => m.trim())
                              .filter(Boolean) })} />
              </F>
            </div>
            {canWrite && (
              <Button className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="apprentice-config-save" disabled={busy === "config"}
                      onClick={() => act("config", () => api.put("/apprenticeship/config", {
                        review_weeks: cfg.review_weeks, final_review: cfg.final_review,
                        default_stipend: cfg.default_stipend,
                        default_duration_weeks: cfg.default_duration_weeks, modules: cfg.modules,
                      }), "Saved")}>
                {busy === "config" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
              </Button>
            )}
          </Panel>
        </TabsContent>
      </Tabs>

      {/* create */}
      <Dialog open={Boolean(creating)} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="max-w-lg" data-testid="apprentice-dialog">
          <DialogHeader>
            <DialogTitle>Add an apprentice</DialogTitle>
            <DialogDescription>
              Their photograph and story are only ever used where consent is given.
            </DialogDescription>
          </DialogHeader>
          {creating && (
            <div className="max-h-[56vh] space-y-3 overflow-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <F label="Full name">
                  <Input value={creating.name} data-testid="apprentice-name"
                         onChange={(e) => setCreating({ ...creating, name: e.target.value })} />
                </F>
                <F label="Trade or field">
                  <Input value={creating.trade} data-testid="apprentice-trade"
                         onChange={(e) => setCreating({ ...creating, trade: e.target.value })} />
                </F>
                <F label="Email">
                  <Input value={creating.email} data-testid="apprentice-email"
                         onChange={(e) => setCreating({ ...creating, email: e.target.value })} />
                </F>
                <F label="Telephone">
                  <Input value={creating.phone} data-testid="apprentice-phone"
                         onChange={(e) => setCreating({ ...creating, phone: e.target.value })} />
                </F>
                <F label="Date of birth">
                  <DateField value={creating.date_of_birth} testId="apprentice-dob"
                             onChange={(v) => setCreating({ ...creating, date_of_birth: v || "" })} />
                </F>
                <F label="Monthly stipend">
                  <Input type="number" value={creating.stipend_monthly}
                         data-testid="apprentice-stipend"
                         onChange={(e) => setCreating({ ...creating,
                           stipend_monthly: e.target.value })} />
                </F>
              </div>
              <F label="Education">
                <Textarea rows={2} value={creating.education} data-testid="apprentice-education"
                          onChange={(e) => setCreating({ ...creating, education: e.target.value })} />
              </F>
              <F label="Family background">
                <Textarea rows={2} value={creating.family_background} data-testid="apprentice-family"
                          onChange={(e) => setCreating({ ...creating,
                            family_background: e.target.value })} />
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Guardian name">
                  <Input value={creating.guardian_name} data-testid="apprentice-guardian"
                         onChange={(e) => setCreating({ ...creating,
                           guardian_name: e.target.value })} />
                </F>
                <F label="Guardian telephone">
                  <Input value={creating.guardian_phone} data-testid="apprentice-guardian-phone"
                         onChange={(e) => setCreating({ ...creating,
                           guardian_phone: e.target.value })} />
                </F>
              </div>
              <F label="Funding source">
                <Input value={creating.funding_source} data-testid="apprentice-funding"
                       onChange={(e) => setCreating({ ...creating,
                         funding_source: e.target.value })} />
              </F>
              <label className="flex items-start gap-2.5 rounded-xl border border-border bg-muted/25 p-3">
                <Checkbox checked={creating.consent_granted} data-testid="apprentice-consent"
                          onCheckedChange={(v) => setCreating({ ...creating,
                            consent_granted: Boolean(v) })} />
                <span className="text-[12.5px] text-foreground">
                  Consent is held for their name, photograph and story.
                </span>
              </label>
              {creating.consent_granted && (
                <div className="flex flex-wrap gap-3 pl-1" data-testid="apprentice-consent-scopes">
                  {SCOPES.map(([code, text]) => (
                    <label key={code} className="flex items-center gap-2">
                      <Checkbox checked={creating.consent_scopes.includes(code)}
                                data-testid={`apprentice-consent-${code}`}
                                onCheckedChange={() => setCreating({
                                  ...creating,
                                  consent_scopes: creating.consent_scopes.includes(code)
                                    ? creating.consent_scopes.filter((s) => s !== code)
                                    : [...creating.consent_scopes, code],
                                })} />
                      <span className="text-[12px] text-muted-foreground">{text}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={create} data-testid="apprentice-save"
                    disabled={busy === "create" || (creating?.name || "").trim().length < 2
                      || (creating?.consent_granted && creating.consent_scopes.length === 0)}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "create" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create the profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* detail */}
      <Dialog open={Boolean(open)} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="apprentice-detail-dialog">
          <DialogHeader>
            <DialogTitle>{open?.name} · {open?.apprentice_ref}</DialogTitle>
            <DialogDescription>
              {open?.trade} · {open?.status.replace(/_/g, " ")}
              {open?.consent?.granted
                ? ` · consent for ${(open.consent.scopes || []).join(", ")}`
                : " · no photo consent"}
            </DialogDescription>
          </DialogHeader>
          {open && (
            <div className="max-h-[60vh] space-y-4 overflow-auto pr-1">
              <div className="flex items-start gap-4">
                {open.photo_url
                  ? <img alt="" data-testid="detail-photo"
                         src={`${API_BASE}${open.photo_url.replace("/api", "")}`}
                         className="h-24 w-24 rounded-xl object-cover" />
                  : <span className="grid h-24 w-24 place-items-center rounded-xl bg-muted text-[12px] text-muted-foreground">
                      No photo
                    </span>}
                <div className="flex-1 space-y-1 text-[12.5px]">
                  <div className="text-muted-foreground">
                    {open.phone} {open.email ? `· ${open.email}` : ""}
                  </div>
                  <div className="text-muted-foreground">{open.address}</div>
                  <div className="text-foreground">{open.education}</div>
                  <div className="text-muted-foreground">{open.family_background}</div>
                  <div className="text-muted-foreground">
                    Funded by {open.funding_source || "—"} · stipend{" "}
                    {money(open.stipend_monthly || 0, "INR")}
                  </div>
                  {canWrite && (
                    <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-3 py-1.5 text-[11.5px] text-muted-foreground hover:text-foreground">
                      <input type="file" accept="image/*" className="hidden"
                             data-testid="apprentice-photo-input"
                             onChange={(e) => uploadPhoto(open.apprentice_ref,
                               e.target.files?.[0])} />
                      {busy === `photo-${open.apprentice_ref}`
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      {open.photo_url ? "Replace the photograph" : "Upload a photograph"}
                    </label>
                  )}
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[1.4px] text-muted-foreground">
                  Placement
                </p>
                {open.placement ? (
                  <div className="mt-1.5 rounded-xl border border-border p-3 text-[12.5px]"
                       data-testid="detail-placement">
                    <div className="text-foreground">
                      {open.placement.department}
                      {open.placement.programme_code ? ` · ${open.placement.programme_code}` : ""}
                    </div>
                    <div className="text-muted-foreground">
                      Supervisor {open.placement.supervisor} ·{" "}
                      {shortDate(open.placement.starts_on)} → {shortDate(open.placement.ends_on)}
                      {open.placement.location ? ` · ${open.placement.location}` : ""}
                    </div>
                  </div>
                ) : (
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-[12.5px] text-muted-foreground">
                      No placement recorded.
                    </span>
                    {canWrite && (
                      <Button size="sm" className="rounded-full text-[11px]"
                              data-testid="start-placement-button"
                              onClick={() => setPlacement({
                                ref: open.apprentice_ref,
                                form: { ...BLANK_PLACEMENT,
                                  funding_source: open.funding_source || "",
                                  stipend_monthly: open.stipend_monthly ?? "" },
                              })}>
                        Start a placement
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[1.4px] text-muted-foreground">
                  Training plan
                </p>
                <div className="mt-1.5 space-y-1.5" data-testid="detail-modules">
                  {(open.training_plan || []).map((m, i) => (
                    <label key={i}
                           className="flex items-center gap-2.5 rounded-xl border border-border px-3 py-1.5">
                      <Checkbox checked={Boolean(m.done)} disabled={!canWrite}
                                data-testid={`module-${i}`}
                                onCheckedChange={(v) => act(`module-${i}`,
                                  () => api.post(
                                    `/apprenticeship/apprentices/${open.apprentice_ref}/modules`,
                                    { index: i, done: Boolean(v) }),
                                  Boolean(v) ? "Module marked complete" : "Module reopened")} />
                      <span className="text-[12.5px] text-foreground">{m.module}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[1.4px] text-muted-foreground">
                  Review milestones
                </p>
                <div className="mt-1.5 space-y-1.5" data-testid="detail-reviews">
                  {(open.reviews || []).length === 0 && (
                    <p className="text-[12.5px] text-muted-foreground">
                      Review points appear once the placement starts.
                    </p>
                  )}
                  {(open.reviews || []).map((r) => (
                    <div key={r.index}
                         className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3 py-2"
                         data-testid={`review-${open.apprentice_ref}-${r.index}`}>
                      <div>
                        <div className="text-[12.5px] text-foreground">{r.label}</div>
                        <div className="text-[11px] text-muted-foreground">
                          due {shortDate(r.due_on)}
                          {r.status === "recorded"
                            ? ` · signed off by ${r.signed_off_by}` : ""}
                        </div>
                        {r.note && (
                          <div className="mt-1 max-w-md text-[11.5px] text-muted-foreground">
                            {r.note}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {r.status === "recorded" ? (
                          <Chip className={OUTCOME_STYLE[r.outcome]}>
                            {r.outcome} · {r.rating}/5
                          </Chip>
                        ) : canWrite ? (
                          <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                                  data-testid={`record-review-${r.index}`}
                                  onClick={() => setReview({ ref: open.apprentice_ref,
                                    index: r.index, label: r.label })}>
                            Record
                          </Button>
                        ) : <Chip className={STATUS_STYLE.applied}>due</Chip>}
                      </div>
                    </div>
                  ))}
                </div>
                {canWrite && open.placement && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <Input value={extra.label} placeholder="Another review point"
                           className="h-8 w-56 text-[12px]" data-testid="extra-review-label"
                           onChange={(e) => setExtra({ ...extra, label: e.target.value })} />
                    <DateField value={extra.due_on} clearable={false} testId="extra-review-date"
                               className="h-8 w-[140px] text-[11.5px]"
                               onChange={(v) => setExtra({ ...extra, due_on: v })} />
                    <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]"
                            data-testid="extra-review-add" disabled={extra.label.trim().length < 3}
                            onClick={() => act("extra", () => api.post(
                              `/apprenticeship/apprentices/${open.apprentice_ref}/reviews`, extra),
                              "Review point added").then(() => setExtra({ label: "",
                                due_on: isoDay(new Date()) }))}>
                      <Plus className="mr-1 h-3 w-3" /> Add
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          {open && canWrite && (
            <DialogFooter className="flex-wrap gap-2">
              {open.status === "applied" && (
                <Button variant="outline" className="rounded-full text-[12px]"
                        data-testid="enrol-button"
                        onClick={() => move(open.apprentice_ref, "enrolled")}>
                  Enrol
                </Button>
              )}
              {["in_placement", "review"].includes(open.status) && (
                <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="complete-apprenticeship"
                        onClick={() => move(open.apprentice_ref, "completed")}>
                  <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Mark completed
                </Button>
              )}
              {open.status === "completed" && (
                <a href={`${API_BASE}/apprenticeship/apprentices/${open.apprentice_ref}/certificate.pdf`}
                   target="_blank" rel="noreferrer" data-testid="apprentice-certificate"
                   className="inline-flex items-center rounded-full bg-primary px-4 py-2 text-[12px] text-primary-foreground transition-opacity duration-150 hover:opacity-90">
                  <Award className="mr-1.5 h-3.5 w-3.5" /> Certificate
                </a>
              )}
              {!["completed", "withdrawn"].includes(open.status) && (
                <Button variant="outline"
                        className="rounded-full text-[12px] text-red-600 dark:text-red-400"
                        data-testid="withdraw-button" onClick={() => setClosing(open)}>
                  Withdraw
                </Button>
              )}
              {open.email && (
                <Button variant="outline" className="rounded-full text-[12px]"
                        data-testid="open-portal-button"
                        onClick={() => { setPortal(open); setPortalPassword(""); }}>
                  {open.portal_enabled ? "Reset their sign-in" : "Give them a sign-in"}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* placement */}
      <Dialog open={Boolean(placement)} onOpenChange={(o) => !o && setPlacement(null)}>
        <DialogContent data-testid="placement-dialog">
          <DialogHeader>
            <DialogTitle>Start a placement</DialogTitle>
            <DialogDescription>
              Review points will be laid out at weeks {(cfg.review_weeks || []).join(", ")}
              {cfg.final_review ? " and a final review at the end" : ""}.
            </DialogDescription>
          </DialogHeader>
          {placement && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <F label="Host department">
                  <Input value={placement.form.department} data-testid="placement-department"
                         onChange={(e) => setPlacement({ ...placement,
                           form: { ...placement.form, department: e.target.value } })} />
                </F>
                <F label="Supervisor">
                  <Input value={placement.form.supervisor} data-testid="placement-supervisor"
                         onChange={(e) => setPlacement({ ...placement,
                           form: { ...placement.form, supervisor: e.target.value } })} />
                </F>
                <F label="Starts">
                  <DateField value={placement.form.starts_on} clearable={false}
                             testId="placement-starts"
                             onChange={(v) => setPlacement({ ...placement,
                               form: { ...placement.form, starts_on: v } })} />
                </F>
                <F label="Ends (optional)" hint={`Defaults to ${cfg.default_duration_weeks} weeks`}>
                  <DateField value={placement.form.ends_on} testId="placement-ends"
                             onChange={(v) => setPlacement({ ...placement,
                               form: { ...placement.form, ends_on: v || "" } })} />
                </F>
              </div>
              <F label="Programme (optional)">
                <Select value={placement.form.programme_code || "none"}
                        onValueChange={(v) => setPlacement({ ...placement,
                          form: { ...placement.form,
                            programme_code: v === "none" ? "" : v } })}>
                  <SelectTrigger data-testid="placement-programme"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No programme</SelectItem>
                    {programmes.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Funding source">
                  <Input value={placement.form.funding_source} data-testid="placement-funding"
                         onChange={(e) => setPlacement({ ...placement,
                           form: { ...placement.form, funding_source: e.target.value } })} />
                </F>
                <F label="Where they will be based">
                  <Input value={placement.form.location} data-testid="placement-location"
                         onChange={(e) => setPlacement({ ...placement,
                           form: { ...placement.form, location: e.target.value } })} />
                </F>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={startPlacement} data-testid="placement-save"
                    disabled={busy === "placement"
                      || (placement?.form.department || "").trim().length < 2
                      || (placement?.form.supervisor || "").trim().length < 2}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "placement" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Start the placement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* review */}
      <Dialog open={Boolean(review)} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent data-testid="review-record-dialog">
          <DialogHeader>
            <DialogTitle>{review?.label}</DialogTitle>
            <DialogDescription>
              A concern or a fail is recorded and flagged on the register. It does not block the
              apprenticeship — it is there so somebody acts on it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <F label="Outcome">
                <Select value={reviewForm.outcome}
                        onValueChange={(v) => setReviewForm({ ...reviewForm, outcome: v })}>
                  <SelectTrigger data-testid="review-outcome"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pass">Pass</SelectItem>
                    <SelectItem value="concerns">Concerns</SelectItem>
                    <SelectItem value="fail">Fail</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Rating out of 5">
                <Input type="number" min="1" max="5" value={reviewForm.rating}
                       data-testid="review-rating"
                       onChange={(e) => setReviewForm({ ...reviewForm,
                         rating: Number(e.target.value) })} />
              </F>
            </div>
            <F label="What the supervisor observed (10 characters or more)">
              <Textarea rows={3} value={reviewForm.note} data-testid="review-note"
                        onChange={(e) => setReviewForm({ ...reviewForm, note: e.target.value })} />
            </F>
            <F label="Evidence reference (optional)">
              <Input value={reviewForm.evidence_reference} data-testid="review-evidence"
                     onChange={(e) => setReviewForm({ ...reviewForm,
                       evidence_reference: e.target.value })} />
            </F>
          </div>
          <DialogFooter>
            <Button onClick={recordReview} data-testid="review-save"
                    disabled={busy === "review" || reviewForm.note.trim().length < 10}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "review" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign off the review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* withdraw */}
      <Dialog open={Boolean(portal)} onOpenChange={(o) => !o && setPortal(null)}>
        <DialogContent data-testid="portal-dialog">
          <DialogHeader>
            <DialogTitle>Sign-in for {portal?.name}</DialogTitle>
            <DialogDescription>
              They sign in at <span className="font-mono">/apprentice/signin</span> with{" "}
              <span className="font-mono">{portal?.email}</span> and see their reviews, training
              plan, diary and certificate. Share the password with them directly — email is not
              connected.
            </DialogDescription>
          </DialogHeader>
          <F label="Password (8 characters or more)">
            <Input type="text" value={portalPassword} data-testid="portal-password"
                   onChange={(e) => setPortalPassword(e.target.value)} />
          </F>
          <DialogFooter>
            <Button data-testid="portal-save" disabled={portalPassword.length < 8
                      || busy === "portal"}
                    onClick={() => act("portal", () => api.post(
                      `/apprenticeship/apprentices/${portal.apprentice_ref}/portal`,
                      { password: portalPassword }),
                      (res) => res.data.message).then((ok) => ok && setPortal(null))}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "portal" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Open their account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(closing)} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw {closing?.name}</DialogTitle>
            <DialogDescription>
              Say why the apprenticeship is ending — it stays on the record.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={reason} data-testid="withdraw-reason"
                    onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button data-testid="withdraw-confirm" disabled={reason.trim().length < 10}
                    onClick={() => move(closing.apprentice_ref, "withdrawn")}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              Record the withdrawal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
